interface Option {
  label: string
  searchValue?: string
  value: string
}

/**
 * Prepends the saved selection when it is not in the loaded pages.
 *
 * Only needed where the value cannot be resolved server-side: getSubaccount is
 * auth_id-keyed and the list API ignores auth_id/account_id/id filters
 * (verified against dev — bogus values still return rows), so `account_id`
 * callers must supply a name from their own record.
 *
 * Own file so a component module does not export non-components.
 */
export interface SavedSelection {
  value: string
  label?: string
}

/**
 * The seed label describes the SAVED selection only. Once the user picks a
 * different subaccount the name no longer belongs to the value, so returning it
 * would print a convincing but wrong name — worse than printing nothing.
 */
export const seedLabelForValue = (
  saved: SavedSelection | null,
  value: string,
): string | undefined => (saved && saved.value === value ? saved.label : undefined)

export interface PairingState {
  /** The value/label pair currently trusted to describe the saved selection. */
  saved: SavedSelection | null
  /** The last props pair observed, so a genuine change can be detected. */
  lastSeen: { value: string; label?: string } | null
}

export const INITIAL_PAIRING: PairingState = { saved: null, lastSeen: null }

/**
 * Decides whether an incoming value/label pair replaces the stored one.
 *
 * The sheets hosting the select stay mounted while the record they edit
 * changes, so the same component sees one saved pair replaced by another. Two
 * changes have to be told apart:
 *
 *   - the parent hydrated a different record -> adopt it, otherwise editing
 *     alert B after alert A leaves B's out-of-page selection blank
 *   - the user picked a different option     -> keep the old pair, so the saved
 *     name is never reprinted against the newly chosen value
 *
 * `changedByUser` is the only reliable discriminator: the select owns onChange,
 * so any change it did not cause came from the parent. Comparing the whole pair
 * rather than just the label also covers two records that share a subaccount
 * name but not its id.
 */
export const reconcileSavedSelection = (
  state: PairingState,
  incoming: { value: string; label?: string },
  changedByUser: boolean,
): PairingState => {
  const unchanged =
    state.lastSeen !== null &&
    state.lastSeen.value === incoming.value &&
    state.lastSeen.label === incoming.label
  if (unchanged) return state

  const lastSeen = { value: incoming.value, label: incoming.label }
  if (changedByUser) return { saved: state.saved, lastSeen }
  // An empty value means "no selection"; keeping a stale pair would seed a
  // label onto whatever value arrives next.
  return {
    saved: incoming.value ? { value: incoming.value, label: incoming.label } : null,
    lastSeen,
  }
}

export const withSelectedSubaccountOption = <T extends Option>(
  options: T[],
  {
    value,
    selectedOptionLabel,
    sentinelValue,
  }: { value: string; selectedOptionLabel?: string; sentinelValue?: string },
): (T | Option)[] => {
  if (!value || !selectedOptionLabel || value === sentinelValue) return options
  if (options.some((option) => option.value === value)) return options
  return [{ label: selectedOptionLabel, searchValue: selectedOptionLabel, value }, ...options]
}
